import { ArrowLeftGlyphIcon } from '@protector/icons';
import { Button, Flex, Modal, Typography } from 'antd';
import { useMediaQuery } from 'beautiful-react-hooks';
import cx from 'classnames';
import { CoreMap } from 'components/map/core-map';
import { MapboxStyle } from 'components/map/core-map.constants';
import { COLOR_OPTIONS } from 'config/style';
import { MEDIA_QUERIES } from 'config/style/breakpoints';
import { ReactComponent as Close } from '@features/regional-overview/assets/digital-traps/close-icon.svg';
import { ReactComponent as LocationOn } from '@features/regional-overview/assets/digital-traps/location_on.svg';
import { DigitalTrapsMapStyles } from '../../../../components/digital-traps-map/digital-traps-map.styles';
import styles from '../network-management.module.css';
import { useNetworkManagementStore } from '../network-management.zustand';
import { useNetworkManagementMap } from './location-modal.hook';

const DESKTOP_MODAL_WIDTH = 600;
const MOBILE_MODAL_WIDTH = '100vw';

const FULLSCREEN_MODAL_STYLE = { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 };

const MODAL_STYLES = {
  content: { padding: 0, overflow: 'hidden' },
  header: { padding: '14px 16px', marginBottom: 0 },
  body: { padding: 0 }
};
const FULLSCREEN_MODAL_STYLES = {
  ...MODAL_STYLES,
  content: { padding: 0, overflow: 'hidden', borderRadius: 0 },
  body: { padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' as const }
};

export const LocationModal = () => {
  const { isModalOpen, trapName, trapId, coordinates, setIsModalOpen } = useNetworkManagementStore();
  const { mapCenter, handleMapbox } = useNetworkManagementMap();
  const isMobile = useMediaQuery(MEDIA_QUERIES.MOBILE);

  const coordinatesLabel = coordinates.length >= 2 ? `${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}` : '';
  const trapIdLabel = `ID: ${trapId}`;

  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Modal
        title={
          <Flex align='center' gap={12}>
            <Button
              type='text'
              aria-label='back'
              onClick={handleClose}
              icon={<ArrowLeftGlyphIcon fill={COLOR_OPTIONS.NEUTRAL_90} />}
              classNames={{ root: styles.backButton }}
            />
            <Flex vertical flex={1} className={styles.headerText}>
              <Typography.Title level={5} ellipsis={{ tooltip: trapName }} style={{ margin: 0 }}>
                {trapName}
              </Typography.Title>
              <Typography.Text type='secondary' ellipsis={{ tooltip: trapIdLabel }} style={{ fontSize: 12 }}>
                {trapIdLabel}
              </Typography.Text>
            </Flex>
            <Button type='link' icon={<Close className={styles.closeIcon} />} onClick={handleClose} aria-label='close' />
          </Flex>
        }
        open={isModalOpen}
        onCancel={handleClose}
        footer={null}
        classNames={{ container: cx(styles.modalContainer, { [styles.modalContainerFullscreen]: isMobile }) }}
        width={isMobile ? MOBILE_MODAL_WIDTH : DESKTOP_MODAL_WIDTH}
        centered={!isMobile}
        style={isMobile ? FULLSCREEN_MODAL_STYLE : undefined}
        styles={isMobile ? FULLSCREEN_MODAL_STYLES : MODAL_STYLES}
        closeIcon={false}>
        <Flex
          vertical
          className={cx(styles.contentBox, { [styles.contentBoxFullscreen]: isMobile, [styles.contentBoxDesktop]: !isMobile })}>
          {coordinatesLabel && (
            <Flex align='center' gap={4} className={styles.coordinatesBadge}>
              <LocationOn className={styles.locationOnIcon} />
              <Typography.Text className={styles.coordinatesText}>{coordinatesLabel}</Typography.Text>
            </Flex>
          )}
          <CoreMap style={MapboxStyle.SATELLITE_STREETS} onMapLoaded={handleMapbox} center={mapCenter} minZoom={5} zoom={15} />
        </Flex>
      </Modal>
      <DigitalTrapsMapStyles />
    </>
  );
};
